/* eslint-disable @typescript-eslint/no-non-null-asserted-optional-chain */
'use client';

import * as React from 'react';
import {
  addDays,
  format,
  differenceInCalendarDays,
  isBefore,
  isSameDay,
  differenceInHours,
} from 'date-fns';
import { Calendar as CalendarIcon, X } from 'lucide-react';
import Popover from '@seller-hermes/features/ui-v2/Popover';
import { Button } from '@seller-hermes/features/ui-v2/Button';
import { cn } from '@seller-hermes/utils/cn';
import Calendar from '@seller-hermes/features/ui-v2/Calendar';
import { DateRange, Day } from 'react-day-picker';
import { dropdownMenuItemVariants } from '@seller-hermes/features/ui-v2/DropdownMenu';
import {
  startOfWeek,
  endOfWeek,
  addMonths,
  endOfMonth,
  startOfMonth,
  startOfDay,
  endOfDay,
  startOfYear,
  endOfYear,
} from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';
import Tooltip from './Tooltip';
import { isAfter } from 'date-fns/esm';

const defineds = {
  startOfWeek: startOfWeek(new Date()),
  endOfWeek: endOfWeek(new Date()),
  startOfLastWeek: startOfWeek(addDays(new Date(), -7)),
  endOfLastWeek: endOfWeek(addDays(new Date(), -7)),
  startOfToday: startOfDay(new Date()),
  endOfToday: endOfDay(new Date()),
  startOfYesterday: startOfDay(addDays(new Date(), -1)),
  endOfYesterday: endOfDay(addDays(new Date(), -1)),
  startOfMonth: startOfMonth(new Date()),
  endOfMonth: endOfMonth(new Date()),
  startOfLastMonth: startOfMonth(addMonths(new Date(), -1)),
  endOfLastMonth: endOfMonth(addMonths(new Date(), -1)),
  startOfThisYear: startOfYear(new Date()),
  endOfThisYear: endOfYear(new Date()),
  startOfLast7Days: startOfDay(addDays(new Date(), -7)),
  startOfLast15Days: startOfDay(addDays(new Date(), -15)),
  startOfLast30Days: startOfDay(addDays(new Date(), -30)),
  startOfLast3Months: startOfMonth(addMonths(new Date(), -3)),
};

interface DateRangePickerProps extends React.HTMLAttributes<HTMLDivElement> {
  onDateRangeChange?: (date: DateRange) => void;
  dateRange?: DateRange;
  contentClassName?: string;
  contentAlign?: 'start' | 'end' | 'center' | undefined;
  minNights?: number;
  maxNights?: number;
  isDisabledDay?: (day: Date) => boolean;
  disablePastDate?: boolean | Date;
  hideRanges?: string[];
  disabled?: boolean;
  /**
   * OPT-IN: com a data inicial escolhida e a final ainda não, DESABILITA os
   * dias que fariam o intervalo passar de `maxNights`. Dia anterior ao início
   * continua livre (ele recomeça a seleção). Ausente = comportamento de sempre:
   * o dia fora do limite só recomeça a seleção.
   */
  disableDaysBeyondMax?: boolean;
}

export const TodayRange = {
  label: 'Hoje',
  range: () => ({
    from: defineds.startOfToday,
    to: defineds.endOfToday,
  }),
};

export const YesterdayRange = {
  label: 'Ontem',
  range: () => ({
    from: defineds.startOfYesterday,
    to: defineds.endOfYesterday,
  }),
};

export const Last7DaysRange = {
  label: 'Últimos 7 dias',
  range: () => ({
    from: defineds.startOfLast7Days,
    to: defineds.endOfToday,
  }),
};

export const Last15DaysRange = {
  label: 'Últimos 15 dias',
  range: () => ({
    from: defineds.startOfLast15Days,
    to: defineds.endOfToday,
  }),
};

export const Last30DaysRange = {
  label: 'Últimos 30 dias',
  range: () => ({
    from: defineds.startOfLast30Days,
    to: defineds.endOfToday,
  }),
};

export const ThisWeekRange = {
  label: 'Essa Semana',
  range: () => ({
    from: defineds.startOfWeek,
    to: defineds.endOfWeek,
  }),
};

export const LastWeekRange = {
  label: 'Última Semana',
  range: () => ({
    from: defineds.startOfLastWeek,
    to: defineds.endOfLastWeek,
  }),
};

export const ThisMonthRange = {
  label: 'Esse Mes',
  range: () => ({
    from: defineds.startOfMonth,
    to: defineds.endOfMonth,
  }),
};

export const LastMonthRange = {
  label: 'Último Mês',
  range: () => ({
    from: defineds.startOfLastMonth,
    to: defineds.endOfLastMonth,
  }),
};

export const Last3MonthsRange = {
  label: 'Últimos 3 Meses',
  range: () => ({
    from: defineds.startOfLast3Months,
    to: defineds.endOfToday,
  }),
};

export const ranges = [
  TodayRange,
  YesterdayRange,
  Last7DaysRange,
  Last15DaysRange,
  Last30DaysRange,
  ThisWeekRange,
  LastWeekRange,
  ThisMonthRange,
  LastMonthRange,
  Last3MonthsRange,
];

const canDayExtendSelection = ({
  day,
  date,
  minNights,
  maxNights,
}: {
  day: Date;
  date: DateRange | undefined;
  minNights: number;
  maxNights: number;
}) => {
  if (!date?.from) return false;
  const daysDifference = differenceInCalendarDays(day, date.from);
  return daysDifference >= minNights && daysDifference <= maxNights;
};

const getIsOutOfRange = ({
  day,
  date,
  maxNights,
}: {
  day: Date;
  date: DateRange | undefined;
  maxNights: number;
}) => {
  if (isBefore(day, date?.from!)) return true;
  if (date?.from) {
    const daysDifference = differenceInCalendarDays(day, date.from);
    return daysDifference > maxNights || daysDifference < -maxNights;
  }
  return false;
};

const DateRangePicker = ({
  className,
  contentClassName,
  contentAlign = 'start',
  dateRange: initialDateRange = ranges[0].range(),
  onDateRangeChange: onFinalDateRangeChange,
  minNights: inputedMinNights = 1,
  maxNights: inputedMaxNights = 31,
  isDisabledDay,
  disablePastDate,
  hideRanges = [],
  disabled = false,
  disableDaysBeyondMax = false,
}: DateRangePickerProps) => {
  const minNights = inputedMinNights - 1;
  const maxNights = inputedMaxNights - 1;
  const [date, setDate] = React.useState<DateRange>({
    from: undefined,
    to: undefined,
  });

  const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);

  React.useEffect(() => {
    setDate(initialDateRange);
  }, [initialDateRange, isPopoverOpen]);

  const isPastDateDisabled = React.useMemo(() => {
    if (disablePastDate === true) {
      return new Date();
    }

    if (disablePastDate instanceof Date) {
      return disablePastDate;
    }

    return undefined;
  }, [disablePastDate]);

  const setDateCorrectly = React.useCallback(
    (day: DateRange, closure?: string) => {
      const setStartOfDay = (day: Date) => {
        const date = new Date(day);
        date.setHours(0, 0, 0, 0);
        return date;
      };
      const setEndOfDay = (day: Date) => {
        const date = new Date(day);
        date.setHours(23, 59, 59, 999);
        return date;
      };
      const newDate = {
        from: day.from ? setStartOfDay(day.from) : undefined,
        to: day.to ? setEndOfDay(day.to) : undefined,
      };
      setDate(newDate);
      // console.table({
      //   title: closure,
      //   previous_from: date.from ? format(date.from, 'dd/MM') : undefined,
      //   previous_to: date.to ? format(date.to, 'dd/MM') : undefined,
      //   from: newDate.from ? format(newDate.from, 'dd/MM') : undefined,
      //   to: newDate.to ? format(newDate.to, 'dd/MM') : undefined,
      // });
    },
    [setDate],
  );

  const setDateAndMonth = React.useCallback((day: DateRange) => {
    setDate(day);
    if (day.from) setMonth(day.from);
  }, []);

  const handleDayClick = React.useCallback(
    (day: Date) => {
      const haveFullSelection = date?.from && date?.to;
      const isExtendable = canDayExtendSelection({
        day,
        date,
        minNights,
        maxNights,
      });

      // console.table({
      //   title: 'new-state',
      //   day: format(day, 'dd/MM'),
      //   from: date?.from ? format(date.from, 'dd/MM') : '',
      //   to: date?.to ? format(date.to, 'dd/MM') : '',
      //   isExtendable,
      //   isSameDay: isSameDay(day, date.from!),
      // });

      if (haveFullSelection && !isExtendable) {
        return setDateCorrectly(
          { from: day, to: undefined },
          'haveFullSelection && !isExtendable',
        );
      }

      const isBeforeFrom = isBefore(day, date.from!);
      const isAfterTo = isAfter(day, date.to!);

      if (isExtendable && isBeforeFrom) {
        return setDateCorrectly(
          { from: day, to: date.to },
          'isExtendable && isBeforeFrom',
        );
      }

      if (isExtendable && isAfterTo) {
        return setDateCorrectly(
          { from: date.from, to: day },
          'isExtendable && isAfterTo',
        );
      }

      if (!date.from) {
        return setDateCorrectly({ from: day, to: undefined }, '!date.from');
      }

      if (
        haveFullSelection &&
        isSameDay(day, date.to!) &&
        !isSameDay(day, date.from)
      ) {
        return setDateCorrectly(
          { from: date.from, to: undefined },
          'haveFullSelection && isSameDay(day, date.to)',
        );
      }
      if (
        haveFullSelection &&
        isSameDay(day, date.from) &&
        isSameDay(day, date.to!)
      ) {
        return setDateCorrectly(
          { from: undefined, to: undefined },
          'haveFullSelection && isSameDay(day, date.from) && isSameDay(day, date.to)',
        );
      }

      if (!date.to && isBefore(day, date.from)) {
        return setDateCorrectly(
          { from: day, to: undefined },
          '!date.to && isBefore(day, date.from)',
        );
      }

      if (date.from && !date.to && getIsOutOfRange({ day, date, maxNights })) {
        return setDateCorrectly(
          { from: day, to: undefined },
          'date.from && !date.to && getIsOutOfRange({ day, date, maxNights })',
        );
      }

      return setDateCorrectly({ from: date.from, to: day }, 'else');
    },
    [date, minNights, maxNights, setDateCorrectly],
  );

  const caption = React.useMemo(() => {
    if (!date?.from) return 'Escolha a data inicial';
    if (!date.to)
      return `Escolha a data final (máximo de ${inputedMaxNights} dias)`;
    const hours = differenceInHours(date.to, date.from);
    const days = Math.round(hours / 24);
    // console.table({
    //   from: format(date.from, 'dd/MM/y HH:mm:ss', { locale: ptBR }),
    //   to: format(date.to, 'dd/MM/y HH:mm:ss', { locale: ptBR }),
    //   hours,
    //   days_divided: hours / 24,
    //   days_floored: Math.floor(hours / 24),
    //   days_rounded: Math.round(hours / 24),
    //   days_trunc: Math.trunc(hours / 24),
    //   days_ceil: Math.ceil(hours / 24),
    //   days,
    // });
    return `Selecionado: ${days} ${days === 1 ? 'dia' : 'dias'}`;
  }, [date, inputedMaxNights]);

  const selectedText = React.useMemo(() => {
    return date?.from ? (
      date.to ? (
        <>
          {format(date.from, 'dd/MM/y', { locale: ptBR })} -{' '}
          {format(date.to, 'dd/MM/y', { locale: ptBR })}
        </>
      ) : (
        <>{format(date.from, 'dd/MM/y', { locale: ptBR })} - __/__/____</>
      )
    ) : (
      <span>Escolha um período</span>
    );
  }, [date]);
  const [month, setMonth] = React.useState(date.from || new Date());

  // Sem o opt-in o Calendar recebe o MESMO `isDisabledDay` de sempre.
  const disabledDays = React.useMemo(() => {
    if (!disableDaysBeyondMax) return isDisabledDay;
    return (day: Date) => {
      if (isDisabledDay?.(day)) return true;
      if (!date?.from || date?.to) return false;
      return differenceInCalendarDays(day, date.from) > maxNights;
    };
  }, [disableDaysBeyondMax, isDisabledDay, date, maxNights]);

  return (
    <Popover onOpenChange={setIsPopoverOpen} open={isPopoverOpen}>
      <Popover.Trigger asChild>
        <Button
          type="button"
          id="date"
          variant="outline"
          disabled={disabled}
          className={cn(
            'w-auto justify-start text-left font-medium',
            !date && 'text-muted-foreground',
            className,
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {selectedText}
        </Button>
      </Popover.Trigger>
      <Popover.Content
        className={cn('w-auto p-0', contentClassName)}
        align={contentAlign}
      >
        <div className="flex flex-col">
          <div className="border-divider flex items-center justify-between border-b p-3">
            <p className="text-muted-foreground flex-1 text-sm">{caption}</p>
            <Button
              type="button"
              variant={'ghost'}
              size={'icon'}
              onClick={() => setIsPopoverOpen(false)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div className="border-divider flex flex-row border-b p-3">
            <div className="bg-popover text-popover-foreground z-50 flex min-w-[10rem] flex-col overflow-hidden p-1">
              {ranges
                .filter((range) => !hideRanges.includes(range.label))
                .map((range, index) => {
                  const isSelected =
                    date.from &&
                    date.to &&
                    isSameDay(date.from, range.range().from) &&
                    isSameDay(date.to, range.range().to);
                  return (
                    <button
                      type="button"
                      key={index}
                      className={cn(
                        dropdownMenuItemVariants(),
                        'px-4',
                        isSelected && 'bg-accent text-accent-foreground',
                      )}
                      onClick={() => setDateAndMonth(range.range())}
                    >
                      {range.label}
                    </button>
                  );
                })}
            </div>
            <Calendar
              initialFocus
              mode="range"
              defaultMonth={date?.from}
              month={month}
              onMonthChange={setMonth}
              selected={date}
              onDayClick={handleDayClick}
              fromDate={isPastDateDisabled}
              numberOfMonths={1}
              locale={ptBR}
              disabled={disabledDays}
              classNames={{
                day_range_start: 'day-range-start rounded-r-none',
                day_range_end: 'day-range-end rounded-l-none',
              }}
              components={{
                Day: (props) => {
                  const isOutOfRange = getIsOutOfRange({
                    day: props.date,
                    date,
                    maxNights,
                  });
                  // const extendable = canDayExtendSelection({
                  //   day: props.date,
                  //   date,
                  //   minNights,
                  //   maxNights,
                  // });
                  return (
                    <Tooltip>
                      <Tooltip.Trigger>
                        <div
                          className={cn(
                            isOutOfRange &&
                              'hover:border-muted-foreground hover:border hover:border-dashed',
                          )}
                        >
                          <Day {...props} />
                        </div>
                      </Tooltip.Trigger>
                      {/* <Tooltip.Content>
                        <span>
                          {extendable ? 'Extender Seleção' : 'Nova Seleção'}
                        </span>
                      </Tooltip.Content> */}
                    </Tooltip>
                  );
                },
              }}
            />
          </div>
          <div className="flex justify-end gap-2 p-3">
            <Button
              type="button"
              variant={'outline'}
              onClick={() => {
                setDateAndMonth(initialDateRange!);
                onFinalDateRangeChange?.(initialDateRange!);
                setIsPopoverOpen(false);
              }}
            >
              Limpar
            </Button>
            <Button
              type="button"
              disabled={!date || !date.from || !date.to}
              onClick={() => {
                setDateAndMonth(date!);
                onFinalDateRangeChange?.(date!);
                setIsPopoverOpen(false);
              }}
            >
              Aplicar
            </Button>
          </div>
        </div>
      </Popover.Content>
    </Popover>
  );
};

export default DateRangePicker;
